import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import { Avatar, FormControl, Typography, TextField, Button } from "@mui/material";
import useAuth  from "../../../hooks/useAuth";
import { User } from "../../../types/userTypes";
import { getDeterministicColor } from "../../../utils/utils";
import Card from "@mui/material/Card";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import LockResetOutlinedIcon from "@mui/icons-material/LockResetOutlined";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Close";

interface UserCardProps {
    changeUserInformation: (user: User, userId: string) => void;
    changePassword: (userId: string) => void;
}

const CARD_BUTTON_HEIGHT = '32px'
const CARD_BUTTON_WIDTH = '170px'

const UserCard: React.FC<UserCardProps> = ({ changeUserInformation, changePassword }: UserCardProps) => {
    const auth = useAuth();

    const [disableInputText, setDisableInputText] = useState<boolean>(true);
    const [userInformation, setUserInformation] = useState<User>({
        email: "",
        id: "",
        name: "",
        roles: [],
        surname: "",
        username: "",
        organizations: []
    });

    const handleChangeInformation = (value: string, name: string) => {
        setUserInformation(prevState => ({
            ...prevState,
            [name]: value,
        }));
    };

    const handelClickSaveInformation = () => {
        setDisableInputText(true);
        changeUserInformation(userInformation, userInformation.id);
    };

    useEffect(() => {
        if (auth.user) {
            setUserInformation({
                organizations: [auth.user.organization],
                name: auth.user.name,
                email: auth.user.email,
                surname: auth.user.surname,
                roles: auth.user.roles,
                id: auth.user.id,
                username: auth.user.username,
                password: auth.user.password
            });
        }
    }, [auth.user]);

    return (
        <Card sx={{
            width: "100%",
            p:2
        }}>
            <Grid container direction="row" spacing={2} sx={{ height: "100%" }}>
                <Grid size={{ xs: 12, md: 2 }} sx={{ height: "100%", display: "flex", justifyContent: "center" }}>
                    <Box sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        width: "100%",
                        height: "100%",
                    }}>
                        <Avatar
                            sx={{
                                bgcolor: getDeterministicColor(userInformation.name || '', userInformation.surname || ''),
                                color: 'white',
                                width: 80,
                                height: 80,
                                fontSize: '2rem',
                                fontWeight: 'bold'
                            }}
                        >
                            {userInformation.name && userInformation.name[0]?.toUpperCase()}{userInformation.surname && userInformation.surname[0]?.toUpperCase()}
                        </Avatar>
                    </Box>
                </Grid>

                <Grid size={{ xs: 12, md: 2 }} sx={{ height: "100%", display: "flex", justifyContent: "center" }}>
                    <Grid container direction="row" spacing={2} sx={{ height: "100%" }}>
                        <Grid size={{ xs: 12}} sx={{ height: "100%", display: "flex", justifyContent: "center" }}>
                            <Typography>Organization:</Typography>
                        </Grid>
                        <Grid size={{ xs: 12}} sx={{ height: "100%", display: "flex", justifyContent: "center" }}>
                            <Typography>{userInformation.organizations && userInformation.organizations[0] ? userInformation.organizations[0].name : ''}</Typography>
                        </Grid>
                    </Grid>
                </Grid>

                <Grid size={{ md: 6, xs: 12 }} sx={{ height: "100%" }}>
                    <Grid container direction="row" spacing={2} sx={{ height: "100%" }}>
                        <Grid size={{ md: 6, xs: 12 }} sx={{ height: "100%" }}>
                            <FormControl fullWidth={true}>
                                <TextField
                                    label={'Name'}
                                    value={userInformation.name}
                                    disabled={disableInputText}
                                    onChange={(e) => handleChangeInformation(e.target.value, "name")}
                                    variant="outlined"
                                    size="small"
                                />
                            </FormControl>
                        </Grid>
                        <Grid size={{ md: 6, xs: 12 }} sx={{ height: "100%" }}>
                            <FormControl fullWidth={true}>
                                <TextField
                                    label={'Surname'}
                                    value={userInformation.surname}
                                    disabled={disableInputText}
                                    onChange={(e) => handleChangeInformation(e.target.value, "surname")}
                                    variant="outlined"
                                    size="small"
                                />
                            </FormControl>
                        </Grid>
                        <Grid size={{ md: 6, xs: 12 }} sx={{ height: "100%" }}>
                            <FormControl fullWidth={true}>
                                <TextField
                                    label={'E-mail'}
                                    value={userInformation.email}
                                    disabled={true}
                                    onChange={() => {}}
                                    variant="outlined"
                                    size="small"
                                />
                            </FormControl>
                        </Grid>
                        <Grid size={{ md: 6, xs: 12 }} sx={{ height: "100%" }}>
                            <FormControl fullWidth={true}>
                                <TextField
                                    label={'Username'}
                                    value={userInformation.username}
                                    disabled={true}
                                    onChange={() => {}}
                                    variant="outlined"
                                    size="small"
                                />
                            </FormControl>
                        </Grid>
                    </Grid>
                </Grid>

                <Grid size={{ md: 2, xs: 12 }} sx={{ height: "100%" }}>
                    <Grid container direction="row" spacing={2} sx={{ height: "100%" }}>
                            <Grid container direction="row" spacing={2} sx={{ height: "100%" }}>
                                <Grid size={{ xs: 12 }} sx={{ height: "100%" }} display={'flex'} justifyContent={'center'}>
                                    {disableInputText ? <Button
                                        sx={{width: CARD_BUTTON_WIDTH, height: CARD_BUTTON_HEIGHT}}
                                        variant="contained"
                                        onClick={() => changePassword(userInformation.id)}
                                        startIcon={<LockResetOutlinedIcon />}
                                    >
                                            Change Password
                                    </Button> :
                                    <Button
                                        sx={{width: CARD_BUTTON_WIDTH, height: CARD_BUTTON_HEIGHT}}
                                    variant="contained"
                                    onClick={() => setDisableInputText(true)}
                                    startIcon={<CancelIcon />}
                                    >
                                        Cancel
                                    </Button>

                                    }
                                </Grid>
                                <Grid size={{ xs: 12 }} sx={{ height: "100%" }} display={'flex'} justifyContent={'center'}>
                                    {disableInputText ? (
                                        <Button
                                            sx={{width: CARD_BUTTON_WIDTH, height: CARD_BUTTON_HEIGHT}} variant="contained"
                                            onClick={() => setDisableInputText(false)}
                                            startIcon={<EditOutlinedIcon />}
                                        >
                                                Update
                                        </Button>
                                    ) : (
                                        <Button
                                            sx={{width: CARD_BUTTON_WIDTH, height: CARD_BUTTON_HEIGHT}}
                                            variant="contained"
                                            onClick={() => handelClickSaveInformation()}
                                            startIcon={<SaveIcon/>}
                                        >
                                                Save
                                        </Button>
                                    )}
                                </Grid>
                            </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </Card>
    );
};

export default UserCard;